import React from 'react';

import {ALREADY_INVITED_MESSAGE} from 'src/constants';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import Typography from '@pb/kit-component-typography';

import {useInviteUserDialog} from '../../hooks/use-invite-user-dialog';

import {AddUserForm} from './add-user-form';

import type {Vendor} from 'src/openapi/admin/models/Vendor';

type AddUserDialogProps = {
    showDialog: boolean,
    closeDialog: () => void,
    vendors: Vendor[],
};


export const AddUserDialog: React.FC<AddUserDialogProps> = ({
    showDialog,
    closeDialog,
    vendors,
}) => {
    const {
        form,
        availableRoles,
        emailInput,
        shown,
        hasSearched,
        searchLoading,
        suggestUsers,
        isMinQueryShown,
        userLookupLoading,
        isInviteDisabled,
        isRoleSelectionDisabled,
        displayError,
        alreadyInvited,
        busy,
        handleEmailChange,
        handleUserSelect,
        clearSuggest,
        setShown,
        handleRoleChange,
        handleVendorChange,
        handleInviteClick,
        handleClose,
    } = useInviteUserDialog({vendors, closeDialog});

    return (
        <Dialog
            shown={showDialog}
            setShown={visible => {
                if (!visible) {
                    handleClose();
                }
            }}>
            <DialogHeader onClose={handleClose}>
                <Typography variant="heading-level-2">
                    Add User
                </Typography>
            </DialogHeader>
            <Group direction="v" style={{padding: 24, width: 400}}>
                {displayError ? (
                    <Message color="error" style={{paddingBottom: 12}}>
                        {displayError}
                    </Message>
                ) : null}
                {alreadyInvited ? (
                    <Message color="error" style={{paddingBottom: 12}}>
                        {ALREADY_INVITED_MESSAGE}
                    </Message>
                ) : null}
                <AddUserForm
                    emailInput={emailInput}
                    roles={availableRoles}
                    vendors={vendors}
                    roleId={form.roleId}
                    vendorId={form.vendorId}
                    disableRole={isRoleSelectionDisabled}
                    firstName={form.firstName}
                    lastName={form.lastName}
                    suggestUsers={suggestUsers}
                    searchLoading={searchLoading}
                    hasSearched={hasSearched}
                    isMinQueryShown={isMinQueryShown}
                    shown={shown}
                    onEmailChange={handleEmailChange}
                    onUserSelect={handleUserSelect}
                    onClearSuggest={clearSuggest}
                    onSetShown={setShown}
                    onHandleRoleChange={handleRoleChange}
                    onHandleVendorChange={handleVendorChange}
                    onBlur={() => setShown(false)}
                />
            </Group>
            <DialogFooter>
                <Button
                    variant="Type.PRIMARY"
                    disabled={isInviteDisabled}
                    busy={busy || searchLoading || userLookupLoading}
                    onClick={handleInviteClick}>
                    Add
                </Button>
                <Button onClick={handleClose} busy={busy} variant="Type.SECONDARY">
                    Cancel
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
